import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Download, FileSpreadsheet, ShieldAlert } from "lucide-react";
import { api, fmtINR } from "@/lib/api";
import { exportDealerStatement } from "@/lib/excelExport";

export default function OutstandingPayments() {
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  useEffect(() => {
    setLoading(true);
    api.get("/outstanding")
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.ceil(rows.length / PAGE_SIZE) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  const totalInvoiced = rows.reduce((acc, r) => acc + (r.total_invoiced || 0), 0);
  const amountPaid = rows.reduce((acc, r) => acc + (r.amount_paid || 0), 0);
  const totalOutstanding = rows.reduce((acc, r) => acc + (r.outstanding || 0), 0);

  const handleExportStatement = () => {
    exportDealerStatement(
      isDealer ? (user?.name || "Dealer Statement") : "All Dealers Outstanding",
      isDealer ? (user?.dealer_id || "DLR") : "ALL",
      rows,
      { total_invoiced: totalInvoiced, amount_paid: amountPaid, outstanding: totalOutstanding }
    );
  };

  return (
    <div className="space-y-6" data-testid="outstanding-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">
            {isDealer ? "My Account" : "All Dealers"}
          </div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
            Outstanding Payments & Statement
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isDealer
              ? "Your current outstanding balance and statement of account with Bhawar Sales Corporation."
              : "Outstanding balances across all dealers. Scoped to your assigned stores or area."}
          </p>
        </div>
        <Button 
          onClick={handleExportStatement}
          disabled={rows.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
        >
          <FileSpreadsheet size={15} className="mr-1.5" />
          Export Statement (.csv / Excel)
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-white">
          <div className="text-xs font-medium text-slate-500">Total Invoiced</div>
          <div className="text-2xl font-semibold mt-1">{fmtINR(rows.reduce((acc, r) => acc + (r.total_invoiced || 0), 0))}</div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="text-xs font-medium text-slate-500">Amount Paid</div>
          <div className="text-2xl font-semibold mt-1 text-emerald-600">{fmtINR(rows.reduce((acc, r) => acc + (r.amount_paid || 0), 0))}</div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="text-xs font-medium text-slate-500">Total Outstanding</div>
          <div className="text-2xl font-semibold mt-1 text-amber-600">{fmtINR(rows.reduce((acc, r) => acc + (r.outstanding || 0), 0))}</div>
        </Card>
      </div>

      {/* Table structure — live data feed */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Summary</div>
            <h2 className="text-lg font-medium tracking-tight mt-0.5" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
              {isDealer ? "My Balance" : "Dealer Balances"}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-emerald-600 font-medium">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Live Data Feed
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                {!isDealer && <th className="text-left px-6 py-3">Dealer</th>}
                {!isDealer && <th className="text-left px-6 py-3">Area</th>}
                <th className="text-right px-6 py-3">Total Invoiced</th>
                <th className="text-right px-6 py-3">Amount Paid</th>
                <th className="text-right px-6 py-3">Outstanding</th>
                <th className="text-right px-6 py-3">Last PO</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={isDealer ? 4 : 6} className="text-center py-12 text-slate-400">Loading balances...</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={isDealer ? 4 : 6} className="text-center py-16 text-slate-400">
                    <div className="flex flex-col items-center gap-3">
                      <AlertCircle size={32} strokeWidth={1} className="text-slate-300" />
                      <div className="text-sm font-medium text-slate-500">No outstanding balances</div>
                      <div className="text-xs text-slate-400 max-w-xs text-center">
                        All invoices and purchase orders are up to date.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((r, i) => (
                  <tr key={r.dealer_id || i} className="hover:bg-slate-50/50">
                    {!isDealer && <td className="px-6 py-3.5 font-medium">{r.dealer_name}</td>}
                    {!isDealer && <td className="px-6 py-3.5 text-xs text-slate-500">{r.dealer_area}</td>}
                    <td className="px-6 py-3.5 text-right">{fmtINR(r.total_invoiced)}</td>
                    <td className="px-6 py-3.5 text-right text-emerald-600">{fmtINR(r.amount_paid)}</td>
                    <td className="px-6 py-3.5 text-right font-semibold text-amber-600">{fmtINR(r.outstanding)}</td>
                    <td className="px-6 py-3.5 text-right font-mono text-xs text-slate-500">{r.last_po || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {rows.length > PAGE_SIZE && (
          <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-medium">{Math.min(page * PAGE_SIZE, rows.length)}</span> of <span className="font-medium">{rows.length}</span> records (100 per page)
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 text-xs">Previous</Button>
              <span className="text-xs text-slate-600 px-2 font-medium">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 text-xs">Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Info card for dealers */}
      {isDealer && (
        <Card className="p-5 bg-slate-50 border-slate-200">
          <div className="text-sm text-slate-600">
            <div className="font-medium text-slate-800 mb-1">How payments work</div>
            <ul className="space-y-1 text-xs">
              <li>• Your Bhawar accounts team records all payments directly in SAP.</li>
              <li>• Outstanding balances shown here will reflect SAP in real-time once the integration is live.</li>
              <li>• For any queries, contact your Area Sales Manager or the accounts team.</li>
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
}
